<template>
  <article class="s-card" :class="{ 's-card--open': open }">
    <!-- Cerrada -->
    <template v-if="!open">
      <header class="s-card__summary-head">
        <img :src="card.icon" alt="" aria-hidden="true" />
        <h3 class="s-card__title">{{ card.titleClosed }}</h3>
      </header>
      <p class="wd-body">{{ card.summary }}</p>
      <button
        type="button"
        class="s-card__toggle"
        :aria-expanded="false"
        :aria-controls="`${card.id}-requirements`"
        ref="openButton"
        @click="toggle(true)"
      >
        <span>Ver requisitos</span>
        <img :src="arrowDown" alt="" aria-hidden="true" />
      </button>
    </template>

    <!-- Abierta -->
    <div v-show="open" :id="`${card.id}-requirements`" class="s-card__panel">
      <header class="s-card__head">
        <div class="s-card__summary-head">
          <img :src="card.icon" alt="" aria-hidden="true" />
          <h3 class="s-card__title">{{ card.titleOpen }}</h3>
        </div>
        <p class="s-card__description" v-html="card.description" />
      </header>

      <ul class="s-card__checks">
        <li v-for="item in card.items" :key="item.text" :class="{ 's-card__check--plain': !item.check }">
          <img v-if="item.check" :src="checkIcon" alt="" aria-hidden="true" />
          <span v-html="item.text" />
        </li>
      </ul>

      <div class="s-card__actions">
        <a
          :href="card.primaryHref"
          class="s-card__action s-card__action--primary"
          :target="card.primaryHref.startsWith('mailto:') ? undefined : '_blank'"
          rel="noopener noreferrer"
          >{{ card.primary }}</a
        >
        <a :href="card.secondaryHref" class="s-card__action s-card__action--secondary">{{ card.secondary }}</a>
      </div>

      <div class="s-card__close">
        <button
          type="button"
          class="s-card__toggle"
          :aria-expanded="true"
          :aria-controls="`${card.id}-requirements`"
          ref="closeButton"
          @click="toggle(false)"
        >
          <span>Ocultar requisitos</span>
          <img :src="arrowUp" alt="" aria-hidden="true" />
        </button>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue'
import checkIcon from '../../../assets/seguros-figma/check-req.svg'
import arrowDown from '../../../assets/seguros-figma/arrow-down.svg'
import arrowUp from '../../../assets/seguros-figma/arrow-up.svg'

export interface ClaimCardItem {
  text: string
  check: boolean
}

export interface ClaimCardData {
  id: string
  primaryHref: string
  secondaryHref: string
  icon: string
  titleClosed: string
  titleOpen: string
  summary: string
  description: string
  items: ClaimCardItem[]
  primary: string
  secondary: string
}

const props = defineProps<{
  card: ClaimCardData
  defaultOpen?: boolean
}>()

const open = ref(Boolean(props.defaultOpen))
const openButton = ref<HTMLButtonElement | null>(null)
const closeButton = ref<HTMLButtonElement | null>(null)
async function toggle(value: boolean) {
  open.value = value
  await nextTick()
  ;(value ? closeButton.value : openButton.value)?.focus({ preventScroll: true })
}
</script>

<style scoped>
.s-card__panel {
  display: flex;
  flex-direction: column;
}

.s-card {
  display: flex;
  flex-direction: column;
  background: var(--ds-grey-50);
  border-radius: 16px;
  box-shadow: 0 10px 7.5px rgba(0, 0, 0, 0.1), 0 4px 3px rgba(0, 0, 0, 0.1);
}

/* Cerrada: card compacta con padding 16 y gap 16 */
.s-card:not(.s-card--open) {
  gap: var(--ds-space-4);
  padding: var(--ds-space-4);
}

.s-card__summary-head {
  display: flex;
  align-items: center;
  gap: var(--ds-space-4);
}
/* Los SVG vienen recortados al glifo: el borde izquierdo queda alineado con el texto y el gap al título es constante */
.s-card__summary-head img {
  flex: 0 0 auto;
  width: auto;
  height: 40px;
}
.s-card__title {
  margin: 0;
  color: var(--ds-text-body);
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.s-card__toggle {
  display: flex;
  align-items: center;
  gap: var(--ds-space-3);
  width: 100%;
  height: 48px;
  padding: 10px var(--ds-space-4);
  box-sizing: border-box;
  border: 0;
  background: transparent;
  color: var(--ds-purple-500);
  font-family: var(--ds-font-family-base);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  text-align: left;
  cursor: pointer;
}
.s-card__toggle span {
  flex: 1 1 auto;
}
.s-card__toggle img {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
}

/* Abierta */
.s-card__head {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
  padding: var(--ds-space-4);
  background: var(--ds-page-bg);
  border-radius: 8px 8px 0 0;
}
.s-card__description {
  margin: 0;
  color: var(--ds-text-body);
  font-size: 14px;
  line-height: 20px;
}

.s-card__checks {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
  margin: 0;
  padding: var(--ds-space-4);
  list-style: none;
}
.s-card__checks li {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  min-height: 24px;
  color: var(--ds-text-body);
  font-size: 12px;
  line-height: 16px;
}
.s-card__checks img {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
}

.s-card__actions {
  display: flex;
  gap: var(--ds-space-3);
  padding: var(--ds-space-4);
}
.s-card__action {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  flex: 1 1 0;
  min-width: 0;
  min-height: 48px;
  padding: 10px var(--ds-space-4);
  box-sizing: border-box;
  border-radius: 24px;
  font-family: var(--ds-font-family-base);
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
}
.s-card__action--primary {
  border: 0;
  background: var(--ds-purple-500);
  color: #fff;
}
.s-card__action--secondary {
  border: 1px solid var(--ds-purple-500);
  background: var(--ds-grey-50);
  color: var(--ds-purple-500);
}

.s-card__close {
  padding: var(--ds-space-4);
  background: var(--ds-page-bg);
  border-radius: 0 0 8px 8px;
}

@media (max-width: 599px) {
  .s-card__actions {
    flex-direction: column;
  }
  .s-card__action {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    flex: none;
    width: 100%;
  }
}
</style>
